import type { EngineContext } from "../../context";
import { isCFSFParticipatingState } from "../../constants/cfsfConstants";
import { XMLBuilder } from "fast-xml-parser";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { Form1099RData } from "./interface/1099-R-interface";
import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";
import {
  generateRandomRecordId,
  generateUniqueTransmissionId,
  generateUniqueSubmissionId,
} from "../../constants/genRecordId";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { parseAmount } from "../../constants/validationFunctions";

// Valid US state codes
const VALID_US_STATES = [
  "AL",
  "AK",
  "AS",
  "AZ",
  "AR",
  "CA",
  "CO",
  "MP",
  "CT",
  "DE",
  "DC",
  "FM",
  "FL",
  "GA",
  "GU",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MH",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PW",
  "PA",
  "PR",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "VI",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "AA",
  "AE",
  "AP",
];

/**
 * Helper function to validate and get a valid state code
 */
function getValidStateCode(state: string | undefined): string | null {
  if (!state) return null;
  const stateCode = state.substring(0, 2).toUpperCase();
  return VALID_US_STATES.includes(stateCode) ? stateCode : null;
}

/**
 * Helper function to format SoftwareId to ensure first two digits match tax year
 * Per IRS requirements, Software IDs must start with the last two digits of the tax year
 */
function formatSoftwareId(
  softwareId: string | undefined,
  taxYear: string
): string {
  const yearSuffix = taxYear.slice(-2);

  if (!softwareId) {
    console.warn(
      `No Software ID provided. Using default. Please use the IRS-assigned Software ID for tax year ${taxYear}.`
    );
    return `${yearSuffix}00000`;
  }

  if (softwareId.startsWith(yearSuffix)) {
    return softwareId.substring(0, 10);
  }

  console.warn(
    `Software ID "${softwareId}" does not start with "${yearSuffix}" for tax year ${taxYear}. ` +
      `Updating to match tax year. Please ensure you're using the IRS-assigned Software ID for ${taxYear}.`
  );

  const remaining = softwareId.substring(2);
  return `${yearSuffix}${remaining}`.substring(0, 10);
}

/**
 * Box 4 as the record carries it.
 *
 * This used to "validate" the withholding by capping it at 25% of the taxable amount or 15%
 * of the gross distribution, and at $5,000, and it read the dollar amounts as cents while
 * doing so. A record with $600 withheld from a $1,000 distribution went to the IRS as $250:
 * the return no longer matched the payee's statement or the money actually withheld. The XML
 * now carries what the record says. Withholding the IRS would treat as excessive is refused
 * before anything is sent, by checkRecords (WITHHOLDING_EXCEEDS_INCOME), where a person sees
 * it and fixes the source.
 */
function federalTaxWithheld(record: Form1099RData): string {
  return parseAmount(record.BOX4_FEDERAL_INCOME_TAX_WITHHELD);
}

function validatePercentage(percentage: string | number | undefined): string {
  if (percentage === undefined || percentage === null || percentage === "") {
    return "0.00"; // Default value
  }

  const numValue =
    typeof percentage === "string" ? parseFloat(percentage) : percentage;

  if (isNaN(numValue)) {
    return "0.00"; // Handle NaN
  }

  // Ensure it's a valid percentage between 0 and 100, converted to decimal (0-1.00)
  return (Math.min(Math.max(0, numValue), 100) / 100).toFixed(2);
}

function validateStateCode(stateCode: string | undefined): string {
  const validStates = [
    "AL",
    "AK",
    "AS",
    "AZ",
    "AR",
    "CA",
    "CO",
    "MP",
    "CT",
    "DE",
    "DC",
    "FM",
    "FL",
    "GA",
    "GU",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MH",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PW",
    "PA",
    "PR",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "VI",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
    "AA",
    "AE",
    "AP",
  ];

  if (!stateCode) return "AA"; // Default value

  // Extract first two characters and convert to uppercase
  const code = stateCode.trim().substring(0, 2).toUpperCase();

  // Return the code if valid, otherwise default to 'AA'
  return validStates.includes(code) ? code : "AA";
}

// Function to validate year (must be 4 digits starting with non-zero)
function validateYear(year: string | undefined): string | undefined {
  if (!year) return undefined;

  // Check if it's already a valid 4-digit year
  if (/^[1-9][0-9]{3}$/.test(year)) {
    return year;
  }

  // Try to convert to a number and validate
  const numYear = parseInt(year, 10);
  if (!isNaN(numYear) && numYear >= 1000 && numYear <= 9999) {
    return numYear.toString();
  }

  // Default to current year if invalid
  return new Date().getFullYear().toString();
}

/**
 * Box 13, date of payment, as YYYY-MM-DD, or undefined when the record has none.
 *
 * PaymentDt is optional in the IRS schema (minOccurs 0), and most 1099-R records leave Box 13
 * empty. This used to throw on an empty value, which failed the whole 1099-R transmission
 * (the correction builder wrote today's date instead). A YYYY-MM-DD value is taken as
 * written: parsing it with new Date() reads it as UTC midnight and, west of Greenwich, gave
 * back the day before.
 */
function formatDate(dateStr: string | undefined): string | undefined {
  const value = String(dateStr ?? "").trim();
  if (!value) return undefined;

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(value);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, "0")}-${iso[3].padStart(2, "0")}`;

  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  if (us) return `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;

  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(value);
  if (compact) return `${compact[1]}-${compact[2]}-${compact[3]}`;

  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return undefined;
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
}

function hasStateTax(record: Form1099RData): boolean {
  return !!(
    record.BOX14_STATE_TAX_WITHHELD1 ||
    record.BOX15_PAYERS_STATE_NO1 ||
    record.BOX16_STATE_DISTRIBUTION1
  );
}

function hasLocalTax(record: Form1099RData): boolean {
  return !!(
    record.BOX17_LOCAL_TAX_WITHHELD1 ||
    record.BOX18_NAME_OF_LOCALITY1 ||
    record.BOX19_LOCAL_DISTRIBUTION1
  );
}

/**
 * Calculate totals grouped by state for CFSF submissions
 * Returns an array of Form1099RTotalByStateGrp objects
 */
function calculateTotalsByState(
  records: Form1099RData[],
  taxYear: string
): Array<{
  StateAbbreviationCd: string;
  TotalReportedRcpntFormCnt: string;
  FederalIncomeTaxWithheldAmt: string;
  GrossDistributionAmt: string;
  TaxableAmt: string;
  CapitalGainAmt: string;
  EmployeeContributionsAmt: string;
  NetUnrlzdSecuritiesApprcnAmt: string;
  ExcessContributionsEarningsAmt?: string;
  OtherDistributionAmt: string;
  TotalEmployeeContributionsAmt: string;
  IRRAllocatedAmt: string;
}> {
  // Group records by state (only CFSF participating states)
  const stateGroups = new Map<string, Form1099RData[]>();

  records.forEach((record) => {
    const stateCode =
      record.RECIPIENT_STATE?.substring(0, 2).toUpperCase() || "XX";
    if (
      stateCode !== "XX" &&
      stateCode.length === 2 &&
      isCFSFParticipatingState(stateCode, taxYear)
    ) {
      if (!stateGroups.has(stateCode)) {
        stateGroups.set(stateCode, []);
      }
      stateGroups.get(stateCode)!.push(record);
    }
  });

  // Calculate totals for each state
  const totalsByState: Array<{
    StateAbbreviationCd: string;
    TotalReportedRcpntFormCnt: string;
    FederalIncomeTaxWithheldAmt: string;
    GrossDistributionAmt: string;
    TaxableAmt: string;
    CapitalGainAmt: string;
    EmployeeContributionsAmt: string;
    NetUnrlzdSecuritiesApprcnAmt: string;
    ExcessContributionsEarningsAmt?: string;
    OtherDistributionAmt: string;
    TotalEmployeeContributionsAmt: string;
    IRRAllocatedAmt: string;
  }> = [];

  stateGroups.forEach((stateRecords, stateCode) => {
    totalsByState.push({
      StateAbbreviationCd: stateCode,
      TotalReportedRcpntFormCnt: stateRecords.length.toString(),
      FederalIncomeTaxWithheldAmt: stateRecords
        .reduce(
          (sum, r) =>
            sum +
            parseFloat(federalTaxWithheld(r)),
          0
        )
        .toFixed(2),
      GrossDistributionAmt: stateRecords
        .reduce(
          (sum, r) =>
            sum + parseFloat(parseAmount(r.BOX1_GROSS_DISTRIBUTION || "0")),
          0
        )
        .toFixed(2),
      TaxableAmt: stateRecords
        .reduce(
          (sum, r) =>
            sum + parseFloat(parseAmount(r.BOX2A_TAXABLE_AMOUNT || "0")),
          0
        )
        .toFixed(2),
      CapitalGainAmt: stateRecords
        .reduce(
          (sum, r) =>
            sum + parseFloat(parseAmount(r.BOX3_CAPITAL_GAIN || "0")),
          0
        )
        .toFixed(2),
      EmployeeContributionsAmt: stateRecords
        .reduce(
          (sum, r) =>
            sum +
            parseFloat(parseAmount(r.BOX5_EMPLOYEE_CONTRIBUTIONS || "0")),
          0
        )
        .toFixed(2),
      NetUnrlzdSecuritiesApprcnAmt: stateRecords
        .reduce(
          (sum, r) =>
            sum +
            parseFloat(parseAmount(r.BOX6_UNREALIZED_APPRECIATION || "0")),
          0
        )
        .toFixed(2),
      // TY2026 (Line 7d) state total, minOccurs=0 - only when any record
      // carries the amount and the schema year supports it
      ...(Number(taxYear) >= 2026 &&
        stateRecords.some(
          (r) => Number(r.BOX7_EARNINGS_EXCESS_CONTRIBUTIONS) > 0
        ) && {
          ExcessContributionsEarningsAmt: stateRecords
            .reduce(
              (sum, r) =>
                sum +
                parseFloat(
                  parseAmount(r.BOX7_EARNINGS_EXCESS_CONTRIBUTIONS || "0")
                ),
              0
            )
            .toFixed(2),
        }),
      OtherDistributionAmt: stateRecords
        .reduce(
          (sum, r) => sum + parseFloat(parseAmount(r.BOX8_OTHER || "0")),
          0
        )
        .toFixed(2),
      TotalEmployeeContributionsAmt: stateRecords
        .reduce(
          (sum, r) =>
            sum +
            parseFloat(
              parseAmount(r.BOX9B_TOT_EMPLOYEE_CONTRIBUTIONS || "0")
            ),
          0
        )
        .toFixed(2),
      IRRAllocatedAmt: stateRecords
        .reduce(
          (sum, r) =>
            sum + parseFloat(parseAmount(r.BOX10_AMOUNT_ALLOCABLE || "0")),
          0
        )
        .toFixed(2),
    });
  });

  return totalsByState;
}

export async function generate1099RXml(
  ctx: EngineContext,
  records: Form1099RData[],
  queryParams?: any
) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    return {
      validationResponse: [
        {
          error: true,
          message: "No records found or records input is invalid.",
        },
      ],
      xml: null,
      taxYear: null,
    };
  }

  // Filter records by fileType if specified in query parameters
  let filteredRecords = records;
  if (queryParams && queryParams.fileType) {
    const targetFileType = queryParams.fileType.toUpperCase();

    // Filter records based on customer file or manual record source
    filteredRecords = records.filter((record) => {
      // Handle different field names in different record sources
      // For 1099-R records, either the form type should match directly
      // or it should be included in the file for this processing year
      return (
        record["1099_YEAR"] &&
        (targetFileType === "1099-R" || targetFileType === "1099R")
      );
    });

    if (filteredRecords.length === 0) {
      return {
        validationResponse: [
          {
            error: true,
            message: `No records found with fileType: ${queryParams.fileType}`,
          },
        ],
        xml: null,
        taxYear: null,
      };
    }
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;

  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: false,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

  // Year stamped in the XML (record year clamped to the IRS intake window) — also
  // drives the Software ID year prefix (IRS requires the filing year's Software ID).
  const currentFilingYear = resolveIrsIntakeTaxYear(filteredRecords[0], ctx.irs.maxIntakeTaxYear);

  // The tax year emitted in every TaxYr element — same clamped value (the XML may
  // never carry a year the IRS intake doesn't accept). The shared fixPriorYearDataInd
  // sanitizer derives the prior-year indicator from the emitted TaxYr downstream.
  const dataTaxYear = resolveIrsIntakeTaxYear(filteredRecords[0], ctx.irs.maxIntakeTaxYear);

  // Determine if this is prior year data
  const isPriorYearData = dataTaxYear !== currentFilingYear;

  // Check if combined federal state program is enabled for this site
  // Get siteId from the first record (all records in a batch should have the same site)
  const firstRecord = filteredRecords[0];
  // EXTRACTION NOTE: the CFSF election used to be two database reads
  // (customerFiles -> site id, then siteYear.combinedFederal_stateProgram).
  // The engine has no database; the host answers through ctx.lookups.cfsfEnabled.
  const isCombinedFederalEnabled = await ctx.lookups.cfsfEnabled(
    firstRecord as unknown as Record<string, unknown>
  );

  const form1099RDetails = filteredRecords.map((record) => {
    // Safely handle string operations
    const safePersonNameControl = record.RECIPIENT_NAME
      ? (record.RECIPIENT_NAME.substring(0, 4) || "").toUpperCase()
      : "";

    const safeLastName = record.RECIPIENT_NAME2 || "UNKNOWN";
    const safeCity = record.RECIPIENT_CITY;

    // Get valid state code for this record
    const validState = getValidStateCode(record.RECIPIENT_STATE);

    // First create the base form details without state/local tax group or payer detail
    const baseFormDetails: any = {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
      TaxYr: dataTaxYear.toString(),
      RecordId: record.id.toString(),
      IssuerOfficeCd: "ABCD", //Need to identify column/key
      ...(isCombinedFederalEnabled &&
        validState &&
        validState !== "AA" &&
        isCFSFParticipatingState(validState, dataTaxYear) && {
          // CFSFElectionStateCd (optional, can repeat up to 62 times per schema)
          // Only include if combined federal state program is enabled AND state is in IRS-provided participating list
          // Uses 2-letter state abbreviation (e.g., "OH", "PA", "MN", "RI") per XML schema requirements
          CFSFElectionStateCd: validState,
        }),
      VoidInd: "0",
      CorrectedInd: record.corrected ? "1" : "0",
      // PrevSubmittedRecRecipientGrp per 2025 schema: UniqueRecordId, TIN, PersonName/BusinessName
      ...(record.corrected && {
        PrevSubmittedRecRecipientGrp: {
          // TIN per 2025 PrevSubmittedRecRecipientGrpType
          TIN: record.RECIPIENT_ID_NUMBER.replace(/-/g, ""),
          // PersonName with PersonFirstNm and PersonLastNm per OtherCompletePersonNameType
          PersonName: {
            PersonFirstNm: record.RECIPIENT_NAME || "UNKNOWN",
            PersonLastNm: record.RECIPIENT_NAME2 || "UNKNOWN",
          },
        },
      }),
      // TY2026 layout-V2 records file via the shared split builder (business
      // as BusinessName+BUSINESS_TIN); legacy records keep the shape below.
      RecipientDetail: buildSplitRecipientDetail(
        record,
        transmitterData.testFileIndicator === "T"
          ? "000000000"
          : record.RECIPIENT_ID_NUMBER.replace(/-/g, ""),
        {
          USAddress: {
            AddressLine1Txt: record.RECIPIENT_ADDRESS1,
            ...(record.RECIPIENT_ADDRESS2 && {
              AddressLine2Txt: record.RECIPIENT_ADDRESS2,
            }),
            CityNm: safeCity,
            StateAbbreviationCd: record.RECIPIENT_STATE,
            ZIPCd: record.RECIPIENT_ZIP,
          },
        }
      ) ?? {
        TIN:
          transmitterData.testFileIndicator === "T"
            ? "000000000"
            : record.RECIPIENT_ID_NUMBER.replace(/-/g, ""),
        TINSubmittedTypeCd: "INDIVIDUAL_TIN",
        ...(safePersonNameControl && {
          PersonNameControlTxt: safePersonNameControl,
        }),
        PersonName: {
          PersonFirstNm: record.RECIPIENT_NAME,
          ...(safeLastName && { PersonLastNm: safeLastName }),
        },
        MailingAddressGrp: {
          USAddress: {
            AddressLine1Txt: record.RECIPIENT_ADDRESS1,
            ...(record.RECIPIENT_ADDRESS2 && {
              AddressLine2Txt: record.RECIPIENT_ADDRESS2,
            }),
            CityNm: safeCity,
            StateAbbreviationCd: record.RECIPIENT_STATE,
            ZIPCd: record.RECIPIENT_ZIP,
          },
        },
      },
      RecipientAccountNum: record.ACCOUNT_NUMBER || "",
      GrossDistributionAmt: parseAmount(record.BOX1_GROSS_DISTRIBUTION),
      TaxableAmt: parseAmount(record.BOX2A_TAXABLE_AMOUNT),
      TxblAmountNotDeterminedInd: record.BOX2B_TAXABLE_AMOUNT_IND ? "1" : "0",
      TotalDistributionInd: record.BOX2B_TOTAL_DISTRIBUTION_IND ? "1" : "0",
      CapitalGainAmt: parseAmount(record.BOX3_CAPITAL_GAIN),
      FederalIncomeTaxWithheldAmt: federalTaxWithheld(record),
      EmployeeContributionsAmt: parseAmount(record.BOX5_EMPLOYEE_CONTRIBUTIONS),
      NetUnrlzdSecuritiesApprcnAmt: parseAmount(
        record.BOX6_UNREALIZED_APPRECIATION
      ),
      Form1099RDistributionCd: record.BOX7_DISTRIBUTION_CODES,
      IRASEPSIMPLEInd: record.BOX7_IRA_SEP_SIMPLE_IND ? "1" : "0",
      // TY2026 additions (Lines 7c/7d) - 2025-clamped transmissions must not
      // contain these elements. TrumpAccountInd is required (no minOccurs) in
      // the TY2026 schema, so always emit 0/1 for 2026+.
      ...(Number(dataTaxYear) >= 2026 && {
        TrumpAccountInd: record.BOX7_TRUMP_ACCOUNT ? "1" : "0",
      }),
      ...(Number(dataTaxYear) >= 2026 &&
        Number(record.BOX7_EARNINGS_EXCESS_CONTRIBUTIONS) > 0 && {
          ExcessContributionsEarningsAmt: parseAmount(
            record.BOX7_EARNINGS_EXCESS_CONTRIBUTIONS
          ),
        }),
      OtherDistributionAmt: parseAmount(record.BOX8_OTHER),
      RcpntOthDistributionPct: validatePercentage(record.BOX8_PERCENT),
      RcpntTotalDistributionPct: validatePercentage(
        record.BOX9A_PERCENT_OF_DISTRIBUTION
      ),
      TotalEmployeeContributionsAmt: parseAmount(
        record.BOX9B_TOT_EMPLOYEE_CONTRIBUTIONS
      ),
      IRRAllocatedAmt: parseAmount(record.BOX10_AMOUNT_ALLOCABLE),
      DesignatedROTHAcctFirstYr: validateYear(record.BOX11_ROTH_CONTRIBUTION),
      FATCAFilingRequirementInd: record.BOX12_FATCA_REQUIREMENT ? "1" : "0",
      PaymentDt: formatDate(record.BOX13_DATE_OF_PAYMENT),
    };

    // Create a complete object with required XML structure
    const completeFormDetails = {
      ...baseFormDetails,

      // Add PayerDetail BEFORE StateLocalTaxGrp (swapped order)
      // PayerDetail: {
      //   PayerId: {
      //     PayerEIN: record.FILER_FEDERAL_ID_NUMBER,
      //   },
      //   PayerNm: record.FILER_NAME,
      //   PayerUSAddress: {
      //     AddressLine1Txt: record.FILER_ADDRESS1,
      //     ...(record.FILER_ADDRESS2 && {
      //       AddressLine2Txt: record.FILER_ADDRESS2,
      //     }),
      //     CityNm: record.FILER_CITY,
      //     StateAbbreviationCd: record.FILER_STATE,
      //     ZIPCd: record.FILER_ZIP,
      //   },
      // },

      // Now include StateLocalTaxGrp AFTER PayerDetail
      StateLocalTaxGrp: {
        // StateAbbreviationCd is mandatory according to the schema
        StateAbbreviationCd: validateStateCode(record.BOX15_PAYERS_STATE_NO1), // Use "AA" as a placeholder if no state code

        // Add StateTaxGrp if we have state tax data
        ...(hasStateTax(record) && {
          StateTaxGrp: {
            StateTaxWithheldAmt: parseAmount(record.BOX14_STATE_TAX_WITHHELD1),
            StateIncomeAmt: parseAmount(record.BOX16_STATE_DISTRIBUTION1),
          },
        }),

        // Add LocalTaxGrp if we have local tax data
        ...(hasLocalTax(record) && {
          LocalTaxGrp: {
            LocalTaxWithheldAmt: parseAmount(record.BOX17_LOCAL_TAX_WITHHELD1),
            LocalIncomeAmt: parseAmount(record.BOX19_LOCAL_DISTRIBUTION1),
          },
        }),
      },
    };

    return completeFormDetails;
  });

  // Generate unique identifiers
  const uniqueTransmissionId = generateUniqueTransmissionId(ctx.irs.tcc);
  const submissionId = generateUniqueSubmissionId();

  // Build the complete envelope structure
  const xmlStructure = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(dataTaxYear),
        UniqueTransmissionId: uniqueTransmissionId,
        TaxYr: dataTaxYear,
        PriorYearDataInd: isPriorYearData
          ? "1"
          : transmitterData.priorYearDataIndicator || "0",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(dataTaxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: 1,
        TotalRecipientFormCnt: form1099RDetails.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: submissionId,
            TaxYr: dataTaxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && {
                ContactPersonNm: transmitterData.contactName,
              }),
              ...(transmitterData.contactTelephoneNumber && {
                ContactPhoneNum: transmitterData.contactTelephoneNumber,
              }),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum,
              }),
            },
            FormTypeCd: "1099R",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: isCombinedFederalEnabled ? "1" : "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: new Date().toISOString().split("T")[0], // Current date in YYYY-MM-DD format
              ...(transmitterData.contactName && {
                JuratPersonTitleTxt: "CFO",
              }),
              ...(transmitterData.contactName && {
                PersonNm: transmitterData.contactName,
              }),
            },
            TotalReportedRcpntFormCnt: form1099RDetails.length.toString(),
            IRSubmission1FormTotals: {
              Form1099RTotalAmtGrp: {
                FederalIncomeTaxWithheldAmt: filteredRecords
                  .reduce(
                    (sum, r) =>
                      sum +
                      parseFloat(federalTaxWithheld(r)),
                    0
                  )
                  .toFixed(2),
                GrossDistributionAmt: filteredRecords
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(parseAmount(r.BOX1_GROSS_DISTRIBUTION)),
                    0
                  )
                  .toFixed(2),
                TaxableAmt: filteredRecords
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(parseAmount(r.BOX2A_TAXABLE_AMOUNT)),
                    0
                  )
                  .toFixed(2),
                CapitalGainAmt: filteredRecords
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(parseAmount(r.BOX3_CAPITAL_GAIN)),
                    0
                  )
                  .toFixed(2),
                EmployeeContributionsAmt: filteredRecords
                  .reduce(
                    (sum, r) =>
                      sum +
                      parseFloat(parseAmount(r.BOX5_EMPLOYEE_CONTRIBUTIONS)),
                    0
                  )
                  .toFixed(2),
                NetUnrlzdSecuritiesApprcnAmt: filteredRecords
                  .reduce(
                    (sum, r) =>
                      sum +
                      parseFloat(parseAmount(r.BOX6_UNREALIZED_APPRECIATION)),
                    0
                  )
                  .toFixed(2),
                // TY2026 (Line 7d) total, minOccurs=0 - only when any record
                // carries the amount and the schema year supports it
                ...(Number(dataTaxYear) >= 2026 &&
                  filteredRecords.some(
                    (r) => Number(r.BOX7_EARNINGS_EXCESS_CONTRIBUTIONS) > 0
                  ) && {
                    ExcessContributionsEarningsAmt: filteredRecords
                      .reduce(
                        (sum, r) =>
                          sum +
                          parseFloat(
                            parseAmount(
                              r.BOX7_EARNINGS_EXCESS_CONTRIBUTIONS || "0"
                            )
                          ),
                        0
                      )
                      .toFixed(2),
                  }),
                OtherDistributionAmt: filteredRecords
                  .reduce(
                    (sum, r) => sum + parseFloat(parseAmount(r.BOX8_OTHER)),
                    0
                  )
                  .toFixed(2),
                TotalEmployeeContributionsAmt: filteredRecords
                  .reduce(
                    (sum, r) =>
                      sum +
                      parseFloat(
                        parseAmount(r.BOX9B_TOT_EMPLOYEE_CONTRIBUTIONS)
                      ),
                    0
                  )
                  .toFixed(2),
                IRRAllocatedAmt: filteredRecords
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(parseAmount(r.BOX10_AMOUNT_ALLOCABLE)),
                    0
                  )
                  .toFixed(2),
              },
              // Form1099RTotalByStateGrp is REQUIRED when CFSFElectionStateCd is used
              ...(isCombinedFederalEnabled && {
                Form1099RTotalByStateGrp: calculateTotalsByState(
                  filteredRecords,
                  dataTaxYear
                ),
              }),
            },
          },
          IRSubmission1Detail: {
            Form1099RDetail: form1099RDetails,
          },
        },
      ],
    },
  };

  try {
    const xml = builder.build(xmlStructure);
    return {
      xml,
      taxYear: dataTaxYear,
      validationResponse: [], // Empty array means no validation errors
    };
  } catch (error) {
    console.error("Error generating XML:", error);
    return {
      validationResponse: [
        {
          error: true,
          message: "Failed to generate XML",
          details: error instanceof Error ? error.message : String(error),
        },
      ],
      xml: null,
      taxYear: dataTaxYear,
    };
  }
}
